import { ConditionalLink } from "components/conditionalLink";
import { ConnectionsModal } from "components/Connections/ConnectionsModal";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { SmallRocket } from "components/icons/SmallRocket";
import { useAppSelector } from "hooks/useAppSelector";
import { useBankConnect } from "hooks/useBankConnect";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useUpdateQuery } from "hooks/useQuery";
import { BooksSubscriptionOnboardingModal } from "components/BooksSubscriptionOnboardingModal";

import BankCircular from "static/images/BankCircular.svg";
import CheckCircle from "static/images/CheckCircle.svg";
import {
  useGetEntityBanksQuery,
  useUpdateBankAccountantAccessMutation,
} from "store/apis/bankConnections";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { BOOKS_PLUS_PLAN } from "constants/addons";
import { BANK_ACCESS_BLOG_URL, SUPPORT_EMAIL } from "constants/base";
import { BANK_ACCESS_HELP } from "constants/task";
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import ToolTip from "components/design/toolTip";
import { Divider } from "components/design/Divider";

export const AccountCard = ({ logo, bankName, accountName, mask }: any) => {
  return (
    <div className="t-border t-shadow-entity-card t-rounded-lg t-border-solid t-border-[#0000001a] t-flex t-items-center t-justify-between t-gap-2.5 t-p-5">
      <div className="t-flex t-items-center t-gap-3">
        <img
          src={logo || BankCircular}
          className="t-w-8 t-rounded-full"
          alt=""
        />
        <div className="t-max-w-[126px] t-text-body-sm t-text-i-primary-100 t-truncate">
          <span>{accountName}</span>
        </div>
        <div className="t-flex t-items-center t-gap-0.5">
          <div className="t-w-2 t-h-2 t-rounded-full t-bg-i-primary-100"></div>
          <div className="t-w-2 t-h-2 t-rounded-full t-bg-i-primary-100"></div>
          <span className="t-text-body-sm">{mask}</span>
        </div>
      </div>
      <img src={CheckCircle} alt="connected" className="t-h-6 t-w-6" />
    </div>
  );
};

type BankGroup = {
  bankName: string;
  logo?: string;
  accounts: {
    uuid: string;
    nickname: string;
    mask: string;
  }[];
  entityItems: {
    uuid: string;
    accountantAccess: boolean;
  }[];
};

export const ConnectBank = () => {
  const group = useCurrentGroupContext();
  const groupId = group?.uuid || "";
  const { update } = useUpdateQuery();
  const { entityId: entityIdOnSelect, product } = useAppSelector(
    (state) => state.onboardingWidget
  );
  const entityId = useCurrentEntityId() || entityIdOnSelect!;
  const isBooksOnboarding = product === "Books";
  const upgradeModal = useModal();
  const handleUpgradeSuccess = () => {
    upgradeModal.close();
  };

  const { data: ledger } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );
  const { accounts = [] } = ledger || {};
  const { onConnect } = useBankConnect();

  const { data: booksPlans = [] } = useGetSubscriptionsQuery(
    {
      subscription_types: BOOKS_PLUS_PLAN,
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const isBooksPlusPlanActive = booksPlans.some(
    ({ group_subscription = [] }) => group_subscription.length > 0
  );

  const uniqueBankConnections = new Set(
    accounts.map(
      ({ bank_brand }) => bank_brand.connection_provider || bank_brand.name
    )
  ).size;

  const isFreePlanBankLimitReached =
    isBooksOnboarding && !isBooksPlusPlanActive && uniqueBankConnections >= 2;

  return (
    <div className="t-max-w-[652px] t-flex t-flex-col t-items-center t-gap-6 t-mx-auto t-p-3">
      <div className="t-flex t-flex-col t-w-full t-gap-5">
        <div className="t-flex t-flex-col t-gap-2">
          <div className="t-text-subtext t-text-text-100">
            Connect data sources
          </div>
          <div className="t-text-body-sm t-text-text-30 t-italic">
            Here’s how it helps: Connect once, and let Inkle auto-fetch and sync
            your transactions - no manual work needed.
          </div>
        </div>
        <div className="t-flex t-items-center t-gap-2 sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
          <div className="t-flex t-items-center t-gap-2 sm:t-w-full">
            {isBooksOnboarding && (
              <span className="t-text-body-sm t-text-text-30">
                Connecting for:
              </span>
            )}{" "}
            <div className="t-contents sm:t-block sm:t-flex-1 sm:t-min-w-0 sm:[&>*]:t-w-full">
              <EntitySelector
                showOnlyUSEntities
                {...(isBooksOnboarding && {
                  handleEntitySelect: (entityId) =>
                    update({ query: "entity", value: entityId }),
                })}
              />
            </div>
          </div>
          {isBooksOnboarding && isBooksPlusPlanActive && (
            <ToolTip
              text={`${
                group.entities?.find((e) => e.uuid === entityId)?.name
              } is currently on Inkle Books Standard Plan`}
            >
              <span className="t-flex t-items-center t-gap-2 sm:t-w-full">
                <Divider
                  classNames="t-self-stretch sm:t-hidden"
                  orientation="vertical"
                />
                <span className="t-text-body-sm t-text-text-30">
                  Currently on
                </span>
                <Tag tagType="purple" icon={false} size="small" rounded>
                  <span className="t-items-center t-gap-1 t-flex">
                    <DiamondPurple />
                    Standard
                  </span>
                </Tag>
              </span>
            </ToolTip>
          )}
        </div>
        {isFreePlanBankLimitReached && isBooksOnboarding && (
          <Banner
            type="info"
            icon={<SmallRocket className="t-size-4 t-text-blue" />}
            title="You've reached your 2-account limit."
            description="To connect more, please upgrade to Standard plan for unlimited connections."
            cta={
              <Button
                size="small"
                customType="primary_outlined"
                onClick={upgradeModal.open}
              >
                Upgrade
              </Button>
            }
          />
        )}
        <div className="t-flex t-flex-col t-gap-3">
          <ConnectionsModal
            showEntityLabel={false}
            onConnect={onConnect}
            disableNewConnections={isFreePlanBankLimitReached}
            disabledTooltip="Free plan is limited to 2 connected accounts. Upgrade to Standard Plan from the dashboard to connect more"
            isOnboarding={isBooksOnboarding}
          />
        </div>
        {accounts.length > 0 && (
          <div className="t-flex t-flex-col">
            <div className="t-text-subtitle-sm">Connected Accounts</div>
            <div className="t-mt-4 t-mb-3 t-flex t-flex-col t-gap-3 t-max-h-72 t-overflow-y-auto">
              {accounts.map((account) => (
                <AccountCard
                  key={account.account.uuid}
                  mask={account.account.mask}
                  bankName={account.bank_brand.name}
                  accountName={account.account.nickname}
                  logo={account.bank_brand.logo}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <BooksSubscriptionOnboardingModal
        open={upgradeModal.isOpen}
        onClose={upgradeModal.close}
        entityId={entityId}
        onSubscribed={handleUpgradeSuccess}
      />
    </div>
  );
};
